// Global error handler utilities

// Store original console methods
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

// List of error messages to suppress (you can customize this)
const ERRORS_TO_SUPPRESS = [
  'ResizeObserver loop limit exceeded',
  'Non-serializable values were found in the state',
  'Warning: Can\'t perform a React state update on an unmounted component',
  'Warning: Each child in a list should have a unique "key" prop',
  'Failed to load resource',
  'Uncaught TypeError',
  'Script error',
  '404 Not Found',
  'validateDOMNesting',
  'button cannot appear as a descendant of button',
  'translate-pa.googleapis.com',
  'Cannot read properties of undefined',
  'POST https://translate-pa.googleapis.com',
  'Bad Request',
  'Uncaught (in promise) TypeError',
  'Uncaught (in promise) Error',
  'Google Translate',
  'translate.googleapis.com',
  'm=el_main',
  'm=el_conf',
  'TypeError: Cannot read properties of undefined (reading \'H\')',
  'Error: bb',
  'Uncaught runtime errors',
  'querySelectorAll.filter is not a function',
  'document.querySelectorAll.filter is not a function',
  'is not iterable',
  'return value is not iterable'
];

// Check if error should be suppressed
const shouldSuppressError = (message) => {
  if (!message || typeof message !== 'string') return false;

  return ERRORS_TO_SUPPRESS.some(suppressedError =>
    message.toLowerCase().includes(suppressedError.toLowerCase())
  );
};

// Override console methods to filter errors
export const initializeErrorSuppression = (suppressInProduction = true) => {
  const shouldSuppress = suppressInProduction && process.env.NODE_ENV === 'production';

  if (shouldSuppress) {
    console.error = (...args) => {
      const message = args.join(' ');
      if (!shouldSuppressError(message)) {
        originalConsoleError.apply(console, args);
      }
    };

    console.warn = (...args) => {
      const message = args.join(' ');
      if (!shouldSuppressError(message)) {
        originalConsoleWarn.apply(console, args);
      }
    };
  }
};

// Global error handler for uncaught JavaScript errors
export const initializeGlobalErrorHandler = (suppressErrors = true) => {
  // Handle uncaught JavaScript errors
  window.addEventListener('error', (event) => {
    if (suppressErrors) {
      // Check if the error should be suppressed
      const errorMessage = event.message || event.error?.message || '';
      if (shouldSuppressError(errorMessage)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }

      if (process.env.NODE_ENV === 'production') {
        event.preventDefault();
        return false;
      }
    }

    // Log error in development only if not suppressed
    if (process.env.NODE_ENV === 'development') {
      const errorMessage = event.message || event.error?.message || '';
      if (!shouldSuppressError(errorMessage)) {
        console.error('Global error caught:', event.error);
      }
    }
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (suppressErrors) {
      // Check if the error should be suppressed
      const errorMessage = event.reason?.message || event.reason?.toString() || '';
      if (shouldSuppressError(errorMessage)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }

      if (process.env.NODE_ENV === 'production') {
        event.preventDefault();
        return false;
      }
    }

    // Log error in development only if not suppressed
    if (process.env.NODE_ENV === 'development') {
      const errorMessage = event.reason?.message || event.reason?.toString() || '';
      if (!shouldSuppressError(errorMessage)) {
        console.error('Unhandled promise rejection:', event.reason);
      }
    }
  });
};

// Initialize both error handling mechanisms
export const initializeErrorHandling = (options = {}) => {
  const {
    suppressInProduction = true,
    suppressConsoleErrors = true,
    suppressGlobalErrors = true
  } = options;

  if (suppressConsoleErrors) {
    initializeErrorSuppression(suppressInProduction);
  }

  if (suppressGlobalErrors) {
    initializeGlobalErrorHandler(suppressInProduction);
  }
};

// Restore original console methods
export const restoreConsole = () => {
  console.error = originalConsoleError;
  console.warn = originalConsoleWarn;
};

// Custom error logger that respects suppression settings
export const logError = (error, context = '') => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}] Error:`, error);
  } else {
    // In production, you might want to send errors to a logging service
    // e.g., Sentry, LogRocket, etc.
    // sendToLoggingService({ error, context });
  }
};

export default {
  initializeErrorHandling,
  initializeErrorSuppression,
  initializeGlobalErrorHandler,
  restoreConsole,
  logError
};
